/**
 * GpsDispositivos — inventario tabular del HW de la flota (IMEI/SIM/firmware).
 * MOCKUP: lista desde gpsApi.devices(). Filtros locales (estado, operador,
 * texto libre). Cada fila enlaza al monitor en vivo con `?device=ID`.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BatteryWarning, ChevronRight, CircleDot, Plus, Radar, Satellite, Search, X } from 'lucide-react';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { cn } from '../../../utils/cn';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Skeleton } from '../../../components/ui/Skeleton';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Table, TBody, TD, TH, THead, TR } from '../../../components/ui/Table';
import { SignalBars } from './components/SignalBars';
import { BatteryGauge } from './components/BatteryGauge';
import { KpiStrip } from './components/KpiStrip';
import IconPicker from './components/IconPicker';
import { estadoMeta, formatNumber, formatRelative, gpsApi } from './gpsMock';

const OPERADORES = ['Entel', 'Movistar', 'Claro', 'WOM'];

// Mapa estado → token de estado (el hex de gpsMock es solo para Leaflet).
const ESTADO_DOT = {
  movimiento: 'bg-status-success',
  detenido:   'bg-status-info',
  inactivo:   'bg-status-warning',
  sin_senal:  'bg-status-neutral',
};

export default function GpsDispositivos() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [devices, setDevices] = useState(null);
  const [search, setSearch] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [operadorFilter, setOperadorFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    // TODO(back): cablear cuando el back exponga /devices.
    gpsApi.devices().then((d) => { if (!cancelled) setDevices(d); });
    return () => { cancelled = true; };
  }, []);

  const data = useMemo(() => {
    if (!devices) return [];
    let arr = devices;
    // `online` es un filtro derivado (lo dispara la KpiStrip, paridad con
    // GpsMonitoreo); el resto coincide 1:1 con d.estado.
    if (estadoFilter === 'online') arr = arr.filter((d) => d.estado !== 'sin_senal');
    else if (estadoFilter) arr = arr.filter((d) => d.estado === estadoFilter);
    if (operadorFilter) arr = arr.filter((d) => d.sim.operador === operadorFilter);
    if (search) {
      const s = search.toLowerCase();
      arr = arr.filter(
        (d) =>
          d.patente.toLowerCase().includes(s) ||
          d.alias.toLowerCase().includes(s) ||
          d.imei.includes(s) ||
          d.modelo.toLowerCase().includes(s) ||
          (d.conductorNombre ?? '').toLowerCase().includes(s),
      );
    }
    return arr;
  }, [devices, estadoFilter, operadorFilter, search]);

  // Conteos derivados de los devices ya cargados (no de `data`, para que la tira
  // refleje el total real de cada estado, no el subconjunto filtrado).
  const counts = useMemo(() => {
    if (!devices) return { total: 0, online: 0, mov: 0, det: 0, inact: 0, off: 0 };
    return {
      total:  devices.length,
      online: devices.filter((d) => d.estado !== 'sin_senal').length,
      mov:    devices.filter((d) => d.estado === 'movimiento').length,
      det:    devices.filter((d) => d.estado === 'detenido').length,
      inact:  devices.filter((d) => d.estado === 'inactivo').length,
      off:    devices.filter((d) => d.estado === 'sin_senal').length,
    };
  }, [devices]);

  const loading = devices === null;
  const hasFilter = search || estadoFilter || operadorFilter;

  // KPIs clicables (misma tira que GpsMonitoreo, sin la card de alarmas).
  // `online` es derivado (estado !== sin_senal); el resto coincide 1:1 con
  // d.estado. 'Dispositivos' (filter '') resetea el filtro.
  const kpiItems = [
    { label: t('Dispositivos'),  value: counts.total,  tone: 'neutral', filter: '',           icon: <Satellite      className="w-3.5 h-3.5" /> },
    { label: t('En línea'),      value: counts.online, tone: 'info',    filter: 'online',      icon: <Radar          className="w-3.5 h-3.5" /> },
    { label: t('En movimiento'), value: counts.mov,    tone: 'success', filter: 'movimiento',  icon: <CircleDot      className="w-3.5 h-3.5" />, dot: ESTADO_DOT.movimiento },
    { label: t('Detenidos'),     value: counts.det,    tone: 'info',    filter: 'detenido',    icon: <CircleDot      className="w-3.5 h-3.5" />, dot: ESTADO_DOT.detenido },
    { label: t('Inactivos'),     value: counts.inact,  tone: 'warning', filter: 'inactivo',    icon: <CircleDot      className="w-3.5 h-3.5" />, dot: ESTADO_DOT.inactivo },
    { label: t('Sin señal'),     value: counts.off,    tone: 'neutral', filter: 'sin_senal',   icon: <BatteryWarning className="w-3.5 h-3.5" />, dot: ESTADO_DOT.sin_senal },
  ];

  return (
    <div className="w-full min-w-0 p-4 md:p-6 space-y-5">
      <PageHeader
        title={t("Inventario de dispositivos")}
        subtitle={t("Hardware GPS de la flota propia · IMEI, SIM, firmware y vínculo a vehículo.")}
        actions={<Button iconLeft={<Plus className="w-4 h-4" />}>{t("Vincular dispositivo")}</Button>}
      />

      <KpiStrip
        items={kpiItems}
        value={estadoFilter}
        onFilter={(f) => setEstadoFilter((prev) => (prev === f ? '' : f))}
      />

      {/* Filtros */}
      <div className="flex flex-col lg:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("IMEI, patente, alias, modelo o conductor…")}
            className="h-10 w-full rounded-lg border border-border bg-bg pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <FilterPills
          label={t("Estado")}
          options={[
            { value: '',           label: t('Todos') },
            { value: 'movimiento', label: t('Movimiento') },
            { value: 'detenido',   label: t('Detenidos') },
            { value: 'inactivo',   label: t('Inactivos') },
            { value: 'sin_senal',  label: t('Sin señal') },
          ]}
          value={estadoFilter}
          onChange={setEstadoFilter}
        />
        <FilterPills
          label={t("Operador")}
          options={[{ value: '', label: t('Todos') }, ...OPERADORES.map((o) => ({ value: o, label: o }))]}
          value={operadorFilter}
          onChange={setOperadorFilter}
        />
        {hasFilter && (
          <Button
            variant="ghost"
            size="md"
            iconLeft={<X className="w-4 h-4" />}
            onClick={() => { setSearch(''); setEstadoFilter(''); setOperadorFilter(''); }}
          >
            {t("Limpiar")}
          </Button>
        )}
      </div>

      <Card padding="none" className="overflow-hidden">
        {loading ? (
          isMobile ? (
            // Skeleton en forma de cards (espeja DeviceCard) para mobile.
            <ul className="divide-y divide-border">
              {Array.from({ length: 6 }).map((_, i) => (
                <li key={i} className="p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Skeleton className="w-2.5 h-2.5 rounded-full" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                    <Skeleton className="h-4 w-16" />
                  </div>
                  <Skeleton className="h-3 w-2/3" />
                  <Skeleton className="h-3 w-1/2" />
                </li>
              ))}
            </ul>
          ) : (
            // Espeja la tabla real (10 columnas) en lugar de barras planas, para
            // que no salte el layout al aparecer la cabecera + filas.
            <TableSkeleton rows={8} columns={10} className="border-0 rounded-none" />
          )
        ) : data.length === 0 ? (
          <EmptyState
            icon={<Satellite />}
            title={t("Sin dispositivos")}
            description={hasFilter ? t('Ningún equipo coincide con los filtros aplicados.') : t('Vincula tu primer equipo GPS a la flota.')}
            action={!hasFilter ? <Button iconLeft={<Plus className="w-4 h-4" />}>{t("Vincular dispositivo")}</Button> : null}
          />
        ) : isMobile ? (
          // MOBILE: una card por dispositivo (patrón _mobile-cards), no scroll-x.
          <ul className="divide-y divide-border">
            {data.map((d) => (
              <DeviceCard key={d.id} device={d} />
            ))}
          </ul>
        ) : (
          <div className="overflow-x-auto">
          <Table>
            <THead>
              <tr>
                <TH>{t("Estado")}</TH>
                <TH>{t("Tipo")}</TH>
                <TH>{t("Patente / Alias")}</TH>
                <TH>{t("IMEI")}</TH>
                <TH>{t("Modelo · Protocolo")}</TH>
                <TH>{t("SIM")}</TH>
                <TH>{t("Señal · Batería")}</TH>
                <TH>{t("Odómetro")}</TH>
                <TH>{t("Último reporte")}</TH>
                <TH aria-label={t("Acciones")}></TH>
              </tr>
            </THead>
            <TBody>
              {data.map((d) => {
                const meta = estadoMeta[d.estado];
                const aging = d.estado === 'sin_senal';
                return (
                  <TR key={d.id}>
                    <TD>
                      <div className="flex items-center gap-2">
                        <span className={cn('w-2 h-2 rounded-full', ESTADO_DOT[d.estado])} aria-hidden />
                        <span className="text-xs text-fg-muted">{t(meta.label)}</span>
                        {d.alarmasActivas.length > 0 && (
                          <Badge tone="error" dot>{d.alarmasActivas.length}</Badge>
                        )}
                      </div>
                    </TD>
                    <TD>
                      <IconPicker device={d} size="sm" />
                    </TD>
                    <TD>
                      <Link
                        to={`/admin/gps-monitoreo?device=${d.id}`}
                        className="font-mono text-sm font-semibold text-fg hover:text-brand-700"
                      >
                        {d.patente}
                      </Link>
                      <div className="text-xs text-fg-muted">{d.alias} · {d.vehiculoMarca} {d.vehiculoModelo}</div>
                    </TD>
                    <TD className="font-mono text-xs text-fg-muted">{d.imei}</TD>
                    <TD>
                      <div className="text-sm text-fg">{d.modelo}</div>
                      <div className="text-xs text-fg-muted">{d.protocolo} · fw {d.firmware}</div>
                    </TD>
                    <TD>
                      <div className="text-sm text-fg">{d.sim.operador}</div>
                      <div className="text-xs text-fg-muted font-mono">{d.sim.numero}</div>
                    </TD>
                    <TD>
                      <div className="flex items-center gap-3">
                        <SignalBars level={d.senalGsm} />
                        <BatteryGauge pct={d.bateriaPct} conectada={d.bateriaConectada} />
                      </div>
                    </TD>
                    <TD className="tabular-nums text-sm text-fg-muted">{formatNumber(d.odometroKm)} km</TD>
                    <TD>
                      <span className={cn('text-sm tabular-nums', aging ? 'text-status-error' : 'text-fg-muted')}>
                        {formatRelative(d.ultimoReporte)}
                      </span>
                    </TD>
                    <TD className="text-right">
                      <Link
                        to={`/admin/gps-monitoreo?device=${d.id}`}
                        className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline"
                      >
                        {t("Ver en mapa")} <ChevronRight className="w-3 h-3" />
                      </Link>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
          </div>
        )}
      </Card>
    </div>
  );
}

// DeviceCard — render mobile de una fila del inventario (reemplaza el scroll-x
// de la tabla de 10 columnas en viewports angostos).
function DeviceCard({ device: d }) {
  const { t } = useTranslation();
  const meta = estadoMeta[d.estado];
  const aging = d.estado === 'sin_senal';
  return (
    <li className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to={`/admin/gps-monitoreo?device=${d.id}`}
            className="font-mono text-sm font-semibold text-fg hover:text-brand-700"
          >
            {d.patente}
          </Link>
          <div className="text-xs text-fg-muted truncate">{d.alias} · {d.vehiculoMarca} {d.vehiculoModelo}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="inline-flex items-center gap-1.5 text-xs text-fg-muted">
            <span className={cn('w-2 h-2 rounded-full', ESTADO_DOT[d.estado])} aria-hidden />
            {t(meta.label)}
          </span>
          {d.alarmasActivas.length > 0 && (
            <Badge tone="error" dot>{d.alarmasActivas.length}</Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <CardField label={t("IMEI")} value={<span className="font-mono">{d.imei}</span>} />
        <CardField label={t("SIM")} value={<span>{d.sim.operador} · <span className="font-mono">{d.sim.numero}</span></span>} />
        <CardField label={t("Modelo · Protocolo")} value={<span>{d.modelo} · {d.protocolo} fw {d.firmware}</span>} />
        <CardField label={t("Odómetro")} value={<span className="tabular-nums">{formatNumber(d.odometroKm)} km</span>} />
        <CardField
          label={t("Señal · Batería")}
          value={
            <span className="flex items-center gap-3">
              <SignalBars level={d.senalGsm} />
              <BatteryGauge pct={d.bateriaPct} conectada={d.bateriaConectada} />
            </span>
          }
        />
        <CardField
          label={t("Último reporte")}
          value={
            <span className={cn('tabular-nums', aging ? 'text-status-error' : 'text-fg')}>
              {formatRelative(d.ultimoReporte)}
            </span>
          }
        />
      </div>

      <div className="flex items-center justify-between pt-1">
        <IconPicker device={d} size="sm" />
        <Link
          to={`/admin/gps-monitoreo?device=${d.id}`}
          className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline"
        >
          {t("Ver en mapa")} <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
    </li>
  );
}

function CardField({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide font-medium text-fg-subtle">{label}</div>
      <div className="text-fg mt-0.5 truncate">{value}</div>
    </div>
  );
}

function FilterPills({ label, options, value, onChange }) {
  // El KpiStrip puede setear un filtro derivado ("online") que no existe como
  // pill discreto; en ese caso resaltamos "Todos" (value '') para que el grupo
  // no quede sin selección visible. Genérico: un value fuera de las opciones
  // recae en "Todos" (en Operador el value siempre está dentro ⇒ sin cambios).
  const known = options.some((opt) => opt.value === value);
  return (
    // flex-wrap: en 390px los pills no caben en una línea; sin wrap se enciman.
    <div className="flex flex-wrap items-center gap-1 bg-bg border border-border rounded-lg p-1">
      <span className="px-2 text-[10px] uppercase tracking-wide font-semibold text-fg-subtle">{label}</span>
      {options.map((opt) => {
        const active = value === opt.value || (opt.value === '' && !known);
        return (
          <button
            key={String(opt.value)}
            onClick={() => onChange(opt.value)}
            className={cn(
              'px-2.5 h-7 text-xs font-medium rounded-md transition-colors',
              active ? 'bg-brand-500 text-white' : 'text-fg-muted hover:text-fg',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
